import React from 'react';
import { useIntl } from 'react-intl';
import MaskedInput from 'react-text-mask';
import { HelpCircle } from 'lucide-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useUser } from '@/contexts/UserContext';
import { DATE_MASK, DATETIME_MASK, masksByCountry } from '@/helpers/mask';
import Tooltip from '@/components/ui-bridge/core/Tooltip';

const MaskedElement = ({ inputRef, mask, ...props }) => {
  const me = useUser();
  const cpfMask = masksByCountry[me?.profile?.country || 'BR'].CPF_MASK;
  const cnpjMask = masksByCountry[me?.profile?.country || 'BR'].CNPJ_MASK;
  const phoneMask = masksByCountry[me?.profile?.country || 'BR'].PHONE_MASK;
  const cellphoneMask = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[me?.profile?.country || 'BR'].CELLPHONE_MASK_LENGTH;

  const masks = {
    cpf: cpfMask,
    cnpj: cnpjMask,
    phone: value => (value.length >= cellphoneMaskLength ? cellphoneMask : phoneMask),
    date: DATE_MASK,
    datetime: DATETIME_MASK,
  };

  return mask ? (
    <MaskedInput {...props} ref={inputRef} guide={false} mask={masks[mask] || null} />
  ) : (
    <input {...props} ref={inputRef} />
  );
};

MaskedElement.propTypes = {
  inputRef: PropTypes.any,
  mask: PropTypes.any,
};

const RequestFormWidgetString = ({
  disabled,
  formData,
  id,
  idSchema,
  label,
  name,
  onChange,
  options,
  placeholder,
  rawErrors,
  required,
  value,
}) => {
  const intl = useIntl();

  const error = rawErrors && rawErrors?.length > 0;
  
  const handleChange = evt => {
    let { value: inputVal } = evt.target;

    if (options?.transform?.includes('remove-whitespace')) {
      inputVal = inputVal.replace(/\s/g, '');
    }

    if (options?.mask === 'cnpj' && typeof inputVal === 'string') {
      inputVal = inputVal.toUpperCase();
    }

    onChange(inputVal);
  };

  const inputPlaceholder = options?.placeholderIsTranslatable && placeholder
    ? intl.formatMessage({ id: placeholder })
    : placeholder || null;

  return (
    <div className="relative w-full">
      <label 
        className="inline-block text-[12px] font-bold text-[#212529] font-['Open_Sans'] mt-[0.3vh] mb-1 tracking-tight" 
        htmlFor={id || idSchema?.$id}
      >
        {intl.formatMessage({ id: label || name })}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <div className="relative flex items-center">
        <MaskedElement
          disabled={disabled}
          type="text"
          name={id || idSchema?.$id}
          id={id || idSchema?.$id}
          data-cy={id || idSchema?.$id}
          maxLength={options?.maxlength}
          mask={options?.mask}
          placeholder={inputPlaceholder}
          value={value || formData || ''}
          onChange={handleChange}
          style={{ fontWeight: 'normal', color: disabled ? 'rgba(0, 0, 0, 0.38)' : '#212529' }}
          className={classNames(
            "w-full rounded-[4px] !h-[37px] !px-[12px] py-[6px] text-[14px] !font-normal font-['Open_Sans'] transition-all focus:outline-none",
            {
              "bg-white": !disabled,
              "!text-[#212529]": !disabled,
              "border-[1px] border-solid border-[#dc3545] focus:border-[#dc3545]": error,
              "border-[1px] border-solid border-[#ced4da] focus:border-[#80bdff]": !error,
              "bg-[#efefef] cursor-not-allowed text-[rgba(0,0,0,0.38)]": disabled
            }
          )}
        />

        {options?.tooltip && (
          <div className="absolute right-3 flex items-center z-10">
            <Tooltip 
              title={intl.formatMessage({ id: options.tooltip?.message })}
              placement="top"
            >
              <HelpCircle size={18} className="text-[#5882c1] cursor-help transition-colors hover:text-[#46699d]" />
            </Tooltip>
          </div>
        )}
      </div>

      {error && (
        <p className="order-form-field-error">
          {rawErrors[0]}
        </p>
      )}
    </div>
  );
};

RequestFormWidgetString.propTypes = {
  disabled: PropTypes.any,
  formData: PropTypes.any,
  id: PropTypes.any,
  idSchema: PropTypes.any,
  label: PropTypes.any,
  name: PropTypes.any,
  onChange: PropTypes.any,
  options: PropTypes.any,
  placeholder: PropTypes.any,
  rawErrors: PropTypes.any,
  required: PropTypes.any,
  value: PropTypes.any,
};

export default RequestFormWidgetString;
